/** Console audit log — paginated/searchable/sortable/filterable grid.
 *
 * Server-side everything via GET /v2/console/audit/. Users without
 * `console_audit_view_all` are scoped to their OWN actions by the backend;
 * the page shows a banner and hides the cross-user filters in that case.
 * Row click opens the full entry incl. pretty-printed payload.
 *
 * Every filter (toolbar AND column-header) lives in the URL query string, so
 * any filter combination is linkable; the details dialog keeps the query
 * intact. Filter writes use history `replace` to avoid one entry per
 * keystroke.
 */
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { getGridStringOperators, type GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useCallback, useMemo } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import * as auditApi from '@/api/audit';
import type { AuditLogEntry } from '@/api/audit';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { config } from '@/config';
import { auditHref, changesHref, consoleRouteFor } from '@/utils/gridLinks';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { TimeWindowPicker, type TimeBound } from '@/components/TimeWindowPicker';
import { UserPicker } from '@/components/UserPicker/UserPicker';
import { usePermissions } from '@/hooks/usePermissions';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { rangeBound, rangeParam, rangeToApi } from './auditRange';
import { DiagnoseAuditSummary, isDiagnoseEvent, isDiagnosePayload } from './DiagnoseAuditSummary';

const fmtTs = (value?: string) =>
  value ? new Date(value).toLocaleString(undefined, { timeZoneName: 'short' }) : '';

const utcTs = (value: string) =>
  `${new Date(value).toISOString().slice(0, 19).replace('T', ' ')} UTC`;

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];
const relFmt = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

function relativeTime(iso: string): string {
  const diffS = (new Date(iso).getTime() - Date.now()) / 1000;
  for (const [unit, secs] of RELATIVE_UNITS) {
    if (Math.abs(diffS) >= secs || unit === 'second') {
      return relFmt.format(Math.round(diffS / secs), unit);
    }
  }
  return '';
}

// The server matches these with icontains — offer only the matching operator.
const containsOnly = getGridStringOperators().filter((op) => op.value === 'contains');

function MapsLink({ location }: { location: string | null | undefined }) {
  if (!location) return null;
  return (
    <Link
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
      target="_blank"
      rel="noopener noreferrer"
      color="inherit"
      onClick={(e) => e.stopPropagation()}
    >
      {location}
    </Link>
  );
}

function UserLink({
  user,
  canLink,
}: {
  user: { id: number; email: string } | null;
  canLink: boolean;
}) {
  if (!user) return null;
  if (!canLink) return <>{user.email}</>;
  return (
    <Link component={RouterLink} to={`/users/${user.id}`} onClick={(e) => e.stopPropagation()}>
      {user.email}
    </Link>
  );
}

/** The entry dialog's quick actions for a user: their page (when the viewer
 *  may open users), mail, copy, and the audit grid filtered by that email. */
function auditUserActions(
  user: { id: number; email: string },
  param: 'actor_email' | 'target_email',
  canLink: boolean,
): ValueAction[] {
  return [
    ...(canLink ? [act.open(`/users/${user.id}`, 'Open user')] : []),
    act.mailto(user.email),
    act.copy(user.email, 'Copy email'),
    act.filter(
      auditHref({ [param]: user.email }),
      param === 'actor_email' ? 'Audit entries by this user' : 'Audit entries about this user',
    ),
  ];
}

const fmtVal = (v: unknown): string =>
  v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);

/** Django-admin change URL for a linked object ("app.model" + id). */
const adminUrlFor = (contentType: string, objectId: number): string | null => {
  const [app, model] = contentType.split('.');
  if (!app || !model) return null;
  return `${config.djangoAdminBaseUrl}/admin/${app}/${model}/${objectId}/change/`;
};

/** The reversion revision recorded for an entry: `payload.revision_id` on
 *  change-audit rows; legacy rows linked the Revision itself. */
const revisionIdOf = (entry: AuditLogEntry): number | null => {
  if (entry.linked_object?.content_type === 'reversion.revision') {
    return entry.linked_object.object_id;
  }
  const raw = entry.payload?.['revision_id'];
  return typeof raw === 'number' ? raw : null;
};

/** "Recorded change" section of the details dialog — the django-reversion
 *  revision linked to the audit entry (per object: field diff, or the full
 *  snapshot for first versions). */
function RevisionPanel({ revisionId }: { revisionId: number }) {
  const q = useQuery({
    queryKey: ['auditRevision', revisionId],
    queryFn: () => auditApi.retrieveRevision(revisionId),
  });
  if (q.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Alert severity="warning">Couldn’t load the recorded change (revision #{revisionId}).</Alert>
    );
  }
  const rev = q.data;
  return (
    <Box>
      <Typography variant="subtitle2">Recorded change (revision #{rev.id})</Typography>
      {rev.comment && (
        <Typography variant="body2" color="text.secondary">
          {rev.comment}
        </Typography>
      )}
      {rev.versions.map((v) => (
        <Box key={v.id} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {(() => {
              const to = consoleRouteFor(v.content_type, v.object_id);
              const text = `${v.content_type} #${v.object_id} — ${v.object_repr}`;
              return to ? (
                <ValueActions actions={[act.open(to, 'Open record'), act.copy(String(v.object_id), 'Copy id')]}>
                  <Link component={RouterLink} to={to} underline="hover">
                    {text}
                  </Link>
                </ValueActions>
              ) : (
                text
              );
            })()}
          </Typography>
          {v.note && (
            <Typography variant="caption" color="text.secondary" component="div">
              {v.note}
            </Typography>
          )}
          {v.changes && Object.keys(v.changes).length > 0 && (
            <Box sx={{ fontFamily: 'monospace', fontSize: 12, mt: 0.5 }}>
              {Object.entries(v.changes).map(([field, ch]) => (
                <Box key={field} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <span>{field}:</span>
                  <Box
                    component="span"
                    sx={{ color: 'error.main', textDecoration: 'line-through' }}
                  >
                    {fmtVal(ch.old)}
                  </Box>
                  <span>→</span>
                  <Box component="span" sx={{ color: 'success.main' }}>
                    {fmtVal(ch.new)}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {v.changes && Object.keys(v.changes).length === 0 && (
            <Typography variant="caption" color="text.secondary" component="div">
              No field-level changes recorded.
            </Typography>
          )}
          {!v.changes && v.fields && (
            <Box
              component="pre"
              sx={{
                m: 0,
                mt: 0.5,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: 12,
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {JSON.stringify(v.fields, null, 2)}
            </Box>
          )}
        </Box>
      ))}
      {rev.versions_truncated && (
        <Typography variant="caption" color="text.secondary">
          … additional objects in this revision are not shown
        </Typography>
      )}
    </Box>
  );
}

function IpLink({ ip }: { ip: string | null | undefined }) {
  if (!ip) return null;
  return (
    <Link
      href={`https://who.is/whois-ip/ip-address/${encodeURIComponent(ip)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {ip}
    </Link>
  );
}

/** One structure for EVERY filter on this page — toolbar widgets and
 *  column-header filters alike. Each entry maps a URL query param to the API
 *  param it feeds; entries with `column` are additionally editable from that
 *  grid column's header filter. The URL is the single source of truth: the
 *  request params (`extra`) and the grid filter model are both derived from
 *  this table. */
const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);
const boolStr = (v: string) => (v === 'true' || v === 'false' ? v === 'true' : undefined);
/** When-column popover presets: filter the range to timestamp ± delta. */
const DELTAS: { label: string; tooltip: string; ms: number }[] = [
  { label: '±1m', tooltip: '±1 minute', ms: 60_000 },
  { label: '±3m', tooltip: '±3 minutes', ms: 3 * 60_000 },
  { label: '±5m', tooltip: '±5 minutes', ms: 5 * 60_000 },
  { label: '±10m', tooltip: '±10 minutes', ms: 10 * 60_000 },
  { label: '±1h', tooltip: '±1 hour', ms: 3_600_000 },
  { label: '±12h', tooltip: '±12 hours', ms: 12 * 3_600_000 },
  { label: '±1d', tooltip: '±1 day', ms: 24 * 3_600_000 },
  { label: '±3d', tooltip: '±3 days', ms: 3 * 24 * 3_600_000 },
];

const FILTERS: FilterDef[] = [
  // One dimension = one param. Where a dimension has BOTH a toolbar widget and
  // a header filter (result, actor, target user) they share the param, so the
  // two surfaces always show the same state: picking a user in the toolbar
  // stores their full email, which is exactly what the header contains-filter
  // edits; a partial string typed in the header shows in the toolbar as text.
  { param: 'success', toApi: boolStr, column: { field: 'success', operator: 'is' } },
  { param: 'actor_email', column: { field: 'actor', operator: 'contains' } },
  { param: 'target_email', column: { field: 'target_user', operator: 'contains' } },
  // Toolbar-only
  { param: 'tenant', apiParam: 'tour_operator', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'from', apiParam: 'created_after', toApi: rangeToApi(false) },
  { param: 'to', apiParam: 'created_before', toApi: rangeToApi(true) },
  // Header-only
  { param: 'event_contains', column: { field: 'event', operator: 'contains' } },
  { param: 'geo', column: { field: 'geo', operator: 'contains' } },
  // URL/chip-only: one background-task run's rows (set via the details dialog,
  // a bgtasks-page action, or a pasted deep link — no free-form widget).
  { param: 'task_id' },
  // URL/chip-only: one onboarding invite's rows (linked from the invite's drawer).
  { param: 'invite', toApi: digits },
];

/** Presets store the audit filter state as `{params: {...}}` over the same
 *  URL params useGridFilters owns. */
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column field order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = ['created_ts', 'event', 'actor', 'target_user', 'tenant', 'success', 'geo'];

/** Minimal shape the user filter autocompletes need. */
type UserOption = { id: number; email: string };

export function AuditLogPage() {
  const { hasPerm } = usePermissions();
  const canViewAll = hasPerm('console_audit_view_all');
  const canOpenUsers = hasPerm('console_user_view'); // /users/:id route gate
  const canOpenTops = hasPerm('console_top_view'); // /tour-operators/:id route gate

  const presets = useGridPresets({
    grid: 'audit',
    defaultOrder: FIELD_ORDER,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  // URL query params are the single source of truth; the shared hook keeps
  // the header-row filters and the toolbar widgets in sync (both read/write
  // the same params) and derives the request `extra` + controlled filterModel.
  // A loaded preset supplies filter values through the preset layer.
  const [searchParams] = useSearchParams();
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);
  // Writes both ends of the When range in one atomic URL update.
  const setRangeParams = useCallback(
    (start: TimeBound | null, end: TimeBound | null) => {
      setParams({ from: rangeParam(start), to: rangeParam(end) });
    },
    [setParams],
  );
  // Toolbar widget values read through the layered getParam so a loaded
  // preset's filters show up in the widgets too (not just in the requests).
  const intParam = (key: string): number | null => {
    const v = getParam(key);
    return v && /^\d+$/.test(v) ? Number(v) : null;
  };
  const rawResult = getParam('success');
  const successFilter: 'all' | 'true' | 'false' =
    rawResult === 'true' || rawResult === 'false' ? rawResult : 'all';
  // The operators the effective tenant filter names (URL > preset > scope); the
  // picker resolves their names itself.
  const tenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const after = getParam('from') ?? '';
  const before = getParam('to') ?? '';
  const taskId = getParam('task_id') ?? '';
  const inviteId = intParam('invite');

  // Actor / Target user values ARE the email params (shared with the header
  // filters), so no lookup is needed — the label is the email itself.
  const actorEmail = searchParams.get('actor_email') ?? '';
  const targetEmail = searchParams.get('target_email') ?? '';
  const actorValue = useMemo<UserOption | null>(
    () => (actorEmail ? { id: 0, email: actorEmail } : null),
    [actorEmail],
  );
  const targetValue = useMemo<UserOption | null>(
    () => (targetEmail ? { id: 0, email: targetEmail } : null),
    [targetEmail],
  );

  // Details dialog is URL-driven: /audit/<eventId> (deep-linkable; the entry is
  // fetched by id, so links work even when the row isn't on the loaded page).
  // The query string rides along so open/close never drops active filters.
  const params = useParams<{ '*': string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const splat = params['*'] ?? '';
  const selectedId = /^\d+$/.test(splat) ? Number(splat) : null;
  const openEntry = useCallback(
    (id: number) => navigate(`/audit/${id}${location.search}`),
    [navigate, location.search],
  );
  const closeEntry = () => navigate(`/audit${location.search}`);

  const detailQuery = useQuery({
    queryKey: ['auditEntry', selectedId],
    queryFn: () => auditApi.retrieve(selectedId!),
    enabled: selectedId !== null,
  });
  const selected = selectedId !== null ? (detailQuery.data ?? null) : null;

  const columns = useMemo<GridColDef<AuditLogEntry>[]>(() => {
    // Shared popover actions. "Open details" appears on every popover; filter
    // actions write the SAME URL params the toolbar/header filters use.
    const detailsAction = (id: number): CellAction => ({
      key: 'details',
      label: 'Open details',
      icon: <InfoOutlinedIcon fontSize="small" />,
      onClick: () => openEntry(id),
    });
    const filterAction = (param: string, value: string, label: string): CellAction => ({
      key: 'filter',
      label,
      icon: <FilterAltIcon fontSize="small" />,
      onClick: () => setParam(param, value),
    });
    const resetAction = (param: string, label: string): CellAction => ({
      key: 'reset',
      label,
      icon: <FilterAltOffIcon fontSize="small" />,
      onClick: () => setParam(param, null),
    });
    const linkAction = (label: string, href: string): CellAction => ({
      key: 'link',
      label,
      icon: <OpenInNewIcon fontSize="small" />,
      href,
    });

    return [
      // filterable: false where the server has no matching per-column param
      // (When has the toolbar range; Tenant has the toolbar autocomplete).
      {
        field: 'created_ts',
        headerName: 'When',
        width: 210,
        filterable: false,
        renderCell: (p) => {
          if (!p.row.created_ts) return null;
          const ts = new Date(p.row.created_ts).getTime();
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.4 }}>
              <span>
                <CellActionsPopover
                  auto
                  title={utcTs(p.row.created_ts)}
                  actions={[
                    detailsAction(p.row.id),
                    ...DELTAS.map(({ label, tooltip, ms }) => ({
                      key: label,
                      label,
                      tooltip: `Filter When to ${tooltip} around this timestamp`,
                      onClick: () => setRangeParams(new Date(ts - ms), new Date(ts + ms)),
                    })),
                    {
                      key: 'reset',
                      label: 'Reset When filter',
                      icon: <FilterAltOffIcon fontSize="small" />,
                      onClick: () => setRangeParams(null, null),
                    },
                  ]}
                >
                  {fmtTs(p.row.created_ts)}
                </CellActionsPopover>
              </span>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {relativeTime(p.row.created_ts)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'event',
        headerName: 'Event',
        width: 260,
        filterOperators: containsOnly,
        renderCell: (p) => (
          <CellActionsPopover
            auto
            actions={[
              detailsAction(p.row.id),
              filterAction(
                'event_contains',
                p.row.event,
                `Filter events containing "${p.row.event}"`,
              ),
              resetAction('event_contains', 'Reset Event filter'),
            ]}
          >
            {p.row.event}
          </CellActionsPopover>
        ),
      },
      {
        field: 'actor',
        headerName: 'Actor',
        flex: 1,
        sortable: false,
        filterOperators: containsOnly,
        valueGetter: (_, row) => row.actor?.email ?? '',
        renderCell: (p) => {
          const u = p.row.actor;
          if (!u) return null;
          return (
            <CellActionsPopover
              auto
              actions={[
                detailsAction(p.row.id),
                filterAction('actor_email', u.email, `Filter by actor "${u.email}"`),
                resetAction('actor_email', 'Reset Actor filter'),
                ...(canOpenUsers ? [linkAction('Open user profile', `/users/${u.id}`)] : []),
              ]}
            >
              {u.email}
            </CellActionsPopover>
          );
        },
      },
      {
        field: 'target_user',
        headerName: 'Target user',
        flex: 1,
        sortable: false,
        filterOperators: containsOnly,
        valueGetter: (_, row) => row.target_user?.email ?? '',
        renderCell: (p) => {
          const u = p.row.target_user;
          if (!u) return null;
          return (
            <CellActionsPopover
              auto
              actions={[
                detailsAction(p.row.id),
                filterAction('target_email', u.email, `Filter by target user "${u.email}"`),
                resetAction('target_email', 'Reset Target user filter'),
                ...(canOpenUsers ? [linkAction('Open user profile', `/users/${u.id}`)] : []),
              ]}
            >
              {u.email}
            </CellActionsPopover>
          );
        },
      },
      {
        field: 'tenant',
        headerName: 'Tenant',
        width: 180,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.tenant?.name ?? (row.tenant ? `#${row.tenant.id}` : ''),
        renderCell: (p) => {
          const tenant = p.row.tenant;
          if (!tenant) return null;
          const label = tenant.name || tenant.code || `#${tenant.id}`;
          return (
            // The audit cells pad themselves (the grid's `py: 1`).
            <WithTenantMark environment={tenant.environment} placement="under" sx={{ py: 0 }}>
              <CellActionsPopover
                auto
                actions={[
                  detailsAction(p.row.id),
                  filterAction('tenant', String(tenant.id), `Filter by tenant "${label}"`),
                  resetAction('tenant', 'Reset Tenant filter'),
                  ...(canOpenTops
                    ? [linkAction('Open tour operator', `/tour-operators/${tenant.id}`)]
                    : []),
                ]}
              >
                {label}
              </CellActionsPopover>
            </WithTenantMark>
          );
        },
      },
      {
        field: 'success',
        headerName: 'Result',
        width: 100,
        type: 'boolean',
        renderCell: (p) =>
          p.row.success ? (
            <Chip size="small" variant="outlined" color="success" label="ok" />
          ) : (
            <Chip size="small" color="error" label="failed" />
          ),
      },
      {
        field: 'geo',
        headerName: 'IP / Geo',
        width: 200,
        filterOperators: containsOnly, // server matches IP OR country OR city
        valueGetter: (_, row) =>
          [row.ip, [row.geo_city, row.geo_country].filter(Boolean).join(', ')]
            .filter(Boolean)
            .join(' '),
        renderCell: (p) => {
          const place = [p.row.geo_city, p.row.geo_country].filter(Boolean).join(', ');
          if (!p.row.ip && !place && !p.row.geo_location) return null;
          const ip = p.row.ip;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.4 }}>
              {ip && (
                <span>
                  <CellActionsPopover
                    auto
                    actions={[
                      detailsAction(p.row.id),
                      filterAction('geo', ip, `Filter by IP "${ip}"`),
                      resetAction('geo', 'Reset IP/Geo filter'),
                      linkAction(
                        'Open whois',
                        `https://who.is/whois-ip/ip-address/${encodeURIComponent(ip)}`,
                      ),
                    ]}
                  >
                    {ip}
                  </CellActionsPopover>
                </span>
              )}
              {place && <span>{place}</span>}
              {p.row.geo_location && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.4 }}
                >
                  <MapsLink location={p.row.geo_location} />
                </Typography>
              )}
            </Box>
          );
        },
      },
    ];
  }, [canOpenUsers, canOpenTops, openEntry, setParam, setRangeParams]);

  // Curated toolbar filters, highest priority collapses last. Result / When /
  // Tenant stay inline longest; the user pickers collapse first on narrow
  // screens. Every dimension is also editable via its header-row filter.
  const toolbarItems: FilterBarItem[] = [
    // Deep-link-only dimension: shown as a dismissible chip when active so the
    // "why is this list so short?" state is visible and clearable.
    ...(inviteId !== null
      ? [
          {
            key: 'invite',
            priority: 6,
            active: true,
            node: (
              <Tooltip title={`Showing onboarding invite #${inviteId}'s rows only. Click to open the invite.`}>
                <Chip
                  label={`Invite #${inviteId}`}
                  onDelete={() => setParam('invite', null)}
                  onClick={() => navigate(`/onboarding-invites/${inviteId}`)}
                />
              </Tooltip>
            ),
          } as FilterBarItem,
        ]
      : []),
    ...(taskId
      ? [
          {
            key: 'task',
            priority: 6,
            active: true,
            node: (
              <Tooltip title={`Showing this background-task run's rows only. Click to open task ${taskId}.`}>
                <Chip
                  label={`Task run ${taskId.slice(0, 8)}…`}
                  onDelete={() => setParam('task_id', null)}
                  onClick={() => navigate(`/bgtasks/${taskId}`)}
                />
              </Tooltip>
            ),
          } as FilterBarItem,
        ]
      : []),
    {
      key: 'result',
      priority: 5,
      active: successFilter !== 'all',
      node: (
        <TextField
          size="small"
          select
          label="Result"
          value={successFilter}
          onChange={(e) => setParam('success', e.target.value === 'all' ? null : e.target.value)}
          sx={{ width: 130 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="true">Success</MenuItem>
          <MenuItem value="false">Failed</MenuItem>
        </TextField>
      ),
    },
    {
      key: 'when',
      priority: 4,
      active: Boolean(after || before),
      node: (
        <TimeWindowPicker
          label="When"
          allowOpenEnded
          value={[rangeBound(after), rangeBound(before, true)]}
          onChange={([start, end]) => setRangeParams(start, end)}
        />
      ),
    },
    {
      key: 'tenant',
      priority: 3,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    ...(canViewAll
      ? [
          {
            key: 'actor',
            priority: 2,
            active: actorValue !== null,
            node: (
              <Box sx={{ minWidth: 260 }}>
                {/* Console actors are system users. */}
                <UserPicker
                  kinds={['system']}
                  value={actorValue}
                  onChange={(u) => setParam('actor_email', u ? u.email : null)}
                  label="Actor"
                  placeholder="Any actor"
                />
              </Box>
            ),
          } as FilterBarItem,
        ]
      : []),
    {
      key: 'target',
      priority: 1,
      active: targetValue !== null,
      node: (
        <Box sx={{ minWidth: 260 }}>
          {/* Targets of console actions can be system or dashboard staff users. */}
          <UserPicker
            kinds={['system', 'staff']}
            value={targetValue}
            onChange={(u) => setParam('target_email', u ? u.email : null)}
            label="Target user"
            placeholder="Any target user"
          />
        </Box>
      ),
    },
  ];

  const listFilters = (params: ResourceDataGridParams) => ({
    // params.ordering only carries column-backed sorts; presets.ordering
    // keeps the default (and preset-saved orderings) alive.
    ordering: params.ordering ?? presets.ordering,
    search: params.search,
    ...(params.extra as Record<string, never>),
    ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
  });

  const queryFn = (params: ResourceDataGridParams) =>
    auditApi.list({
      ...listFilters(params),
      limit: params.pageSize,
      offset: (params.page - 1) * params.pageSize,
      ...(params.countMode ? { count_mode: params.countMode } : {}),
    });

  const countOnly = (params: ResourceDataGridParams) => auditApi.count(listFilters(params));

  return (
    <>
      <PageHeader
        helpStory="audit-log"
        title="Audit Log"
        subtitle="Every Ops Console action: who did what, to whom, and when."
      />
      {!canViewAll && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are seeing only your own actions. Viewing other users&apos; actions requires the
          &quot;view audit actions of all users&quot; permission.
        </Alert>
      )}

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<AuditLogEntry>
          queryKey={['auditLog']}
          queryFn={queryFn}
          countMode={{ grid: 'audit', countOnly }}
          allowExtendedTimeout
          columns={orderColumns(columns, presets.orderedFields)}
          defaultPageSize={50}
          extra={extra}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Event, email or IP',
            exportFileName: 'audit-log',
            presets: presets.toolbar,
          }}
          rowHeight={68}
          sx={{
            // Compact log style: small type, everything anchored to the row top.
            // lineHeight must be reset too — the grid defaults cell line-height
            // to the row height, which re-centers single-line cells vertically.
            '& .MuiDataGrid-cell': {
              fontSize: 12,
              lineHeight: 1.4,
              alignItems: 'flex-start',
              py: 1,
            },
          }}
        />
      )}

      {selectedId !== null && !selected && (
        <Dialog open onClose={closeEntry} maxWidth="md" fullWidth>
          <DialogTitle>Audit entry #{selectedId}</DialogTitle>
          <DialogContent>
            {detailQuery.isError ? (
              <Alert severity="error">
                Couldn’t load this audit entry — it may not exist, or it is another user&apos;s
                action and you lack the view-all permission.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
      {selected && (
        <Dialog open onClose={closeEntry} maxWidth="md" fullWidth>
          <DialogTitle>
            <ValueActions
              actions={[
                act.copy(selected.event, 'Copy event'),
                act.filter(auditHref({ event_contains: selected.event }), 'Audit entries of this event'),
              ]}
            >
              {selected.event}
            </ValueActions>
            <Typography variant="body2" color="text.secondary" component="div">
              {fmtTs(selected.created_ts)} —{' '}
              {selected.actor ? (
                <ValueActions actions={auditUserActions(selected.actor, 'actor_email', canOpenUsers)}>
                  <UserLink user={selected.actor} canLink={canOpenUsers} />
                </ValueActions>
              ) : (
                'system'
              )}
              {selected.success ? '' : ' — FAILED'}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1}>
              {selected.target_user && (
                <Typography variant="body2">
                  Target user:{' '}
                  <ValueActions actions={auditUserActions(selected.target_user, 'target_email', canOpenUsers)}>
                    <UserLink user={selected.target_user} canLink={canOpenUsers} />
                  </ValueActions>
                </Typography>
              )}
              {selected.tenant && (
                <Typography variant="body2">
                  Tenant:{' '}
                  <ValueActions
                    actions={[
                      act.open(`/tour-operators/${selected.tenant.id}`, 'Open operator'),
                      act.filter(auditHref({ tenant: selected.tenant.id }), 'Audit entries of this operator'),
                      ...(selected.tenant.code ? [act.copy(selected.tenant.code, 'Copy operator code')] : []),
                    ]}
                  >
                    <Link component={RouterLink} to={`/tour-operators/${selected.tenant.id}`}>
                      {selected.tenant.name ?? selected.tenant.code ?? `#${selected.tenant.id}`}
                    </Link>
                  </ValueActions>
                </Typography>
              )}
              {selected.ip && (
                <Typography variant="body2">
                  IP:{' '}
                  <ValueActions
                    actions={[
                      act.copy(selected.ip, 'Copy IP'),
                      act.filter(auditHref({ geo: selected.ip }), 'Audit entries from this IP'),
                    ]}
                  >
                    <IpLink ip={selected.ip} />
                  </ValueActions>
                  {(selected.geo_city || selected.geo_country || selected.geo_location) && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' ('}
                      {selected.geo_city ? (
                        <ValueActions
                          actions={[act.filter(auditHref({ geo: selected.geo_city }), 'Audit entries from this city')]}
                        >
                          {[selected.geo_city, selected.geo_country].filter(Boolean).join(', ')}
                        </ValueActions>
                      ) : (
                        selected.geo_country
                      )}
                      {selected.geo_location && (
                        <>
                          {selected.geo_city || selected.geo_country ? ', ' : ''}
                          <MapsLink location={selected.geo_location} />
                        </>
                      )}
                      {')'}
                    </Typography>
                  )}
                </Typography>
              )}
              {selected.user_agent && (
                <Typography variant="body2" color="text.secondary">
                  {selected.user_agent}
                </Typography>
              )}
              {selected.task_id && (
                <Typography variant="body2">
                  Background task:{' '}
                  <Link component={RouterLink} to={`/bgtasks/${selected.task_id}`}>
                    <Box component="code" sx={{ fontSize: 12 }}>
                      {selected.task_id}
                    </Box>
                  </Link>
                  {' — '}
                  {/* No id splat in the target → the dialog closes on navigate. */}
                  <Link component={RouterLink} to={auditHref({ task_id: selected.task_id })}>
                    show this run&apos;s audit trail
                  </Link>
                  {' · '}
                  <Link component={RouterLink} to={changesHref({ task_id: selected.task_id })}>
                    its record changes
                  </Link>
                </Typography>
              )}
              {isDiagnoseEvent(selected.event) && isDiagnosePayload(selected.payload) && (
                <DiagnoseAuditSummary payload={selected.payload} event={selected.event} />
              )}
              {selected.linked_object &&
                selected.linked_object.content_type !== 'reversion.revision' &&
                (selected.linked_object.content_type === 'demo.tour_operator_config_change' &&
                selected.tenant ? (
                  <Typography variant="body2">
                    Change details:{' '}
                    <Link
                      component={RouterLink}
                      to={`/tour-operators/${selected.tenant.id}/configuration/history/${selected.linked_object.object_id}`}
                    >
                      config change #{selected.linked_object.object_id}
                    </Link>
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Changed object: {selected.linked_object.content_type} #
                    {selected.linked_object.object_id}
                    {/* the console page when the type has one; deletions have no page left */}
                    {!selected.event.endsWith('.deleted') &&
                      consoleRouteFor(
                        selected.linked_object.content_type,
                        selected.linked_object.object_id,
                      ) && (
                        <>
                          {' — '}
                          <Link
                            component={RouterLink}
                            to={
                              consoleRouteFor(
                                selected.linked_object.content_type,
                                selected.linked_object.object_id,
                              ) as string
                            }
                          >
                            open in the console
                          </Link>
                        </>
                      )}
                    {/* no admin link for deletions — the change page is gone */}
                    {!selected.event.endsWith('.deleted') &&
                      adminUrlFor(
                        selected.linked_object.content_type,
                        selected.linked_object.object_id,
                      ) && (
                        <>
                          {' — '}
                          <Link
                            href={
                              adminUrlFor(
                                selected.linked_object.content_type,
                                selected.linked_object.object_id,
                              ) as string
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            open in Django admin
                          </Link>
                        </>
                      )}
                  </Typography>
                ))}
              {revisionIdOf(selected) !== null && (
                <RevisionPanel revisionId={revisionIdOf(selected) as number} />
              )}
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: 12,
                  overflow: 'auto',
                  maxHeight: 420,
                }}
              >
                {selected.payload ? JSON.stringify(selected.payload, null, 2) : '(no payload)'}
              </Box>
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
